'use client';

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import html2pdf from 'html2pdf.js';
import { MdDownload, MdOutlineHourglassTop } from 'react-icons/md';
import { useAppDispatch } from '../../app/hooks';
import type { RootState } from '../../app/store';
import BarchartComp from '../../components/barChart/BarChart';
import Button from '../../components/button/Button';
import { EarningInfoBox } from '../../components/earningInfoBox/EarningInfoBox';
import HistoryRouting from '../../components/HistoryRouting';
import type { FilterByDateWithFlag } from '../../components/lineChart/LineChart';
import Spinner from '../../components/spinner/Spinner';
import Table from '../../components/table/Table';
import {
  GetDashboardEarningInfo,
  GetDashboardInfo,
  GetGraphPartnerPayouts,
} from '../../features/partnerSlice';
import {
  ExportDonationReport,
  GetDonationReports,
  GetGraphCampaignData,
  GetGraphDonationTotals,
  GetGraphMemberEarnings,
  GetGraphPartnerEarnings,
  setDonationReports,
  setGraphCampaignData,
  setGraphDonationTotals,
  setGraphMemberEarnings,
  setGraphPartnerEarnings,
} from '../../features/reportingSlice';
import { useEndpointLoading } from '../../hooks/useApiLoading';
import { api } from '../../utils/api/apiClient';
import { useLocation } from 'react-router';
import { formatDateString } from '../../assets/helper';

type DonationReportRow = {
  name: string;
  total: number | string;
  donors: number | string;
  currency?: string;
  payoutId?: number | null;
};

const donationReportsConfig = {
  headers: [
    { key: 'month', label: 'Month' },
    { key: 'total', label: 'Total' },
    { key: 'donors', label: 'Donors' },
  ],
};

const formatDonationAmount = (
  value: number | string | undefined | null,
  currency = 'USD'
): string => {
  if (value === undefined || value === null || value === '') return '$0.00';
  if (typeof value === 'string' && value.trim().startsWith('$')) return value;
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) return String(value);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(numeric);
  } catch {
    return `$${numeric.toFixed(2)}`;
  }
};

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const formatDonorAmount = (amount: unknown) => {
  if (typeof amount === 'number') return `$${amount.toFixed(2)}`;
  if (typeof amount === 'string' && amount.trim()) {
    return amount.trim().startsWith('$') ? amount : `$${amount}`;
  }
  return '$0.00';
};

const downloadDonationMonthReport = async ({
  programName,
  month,
  total,
  donors,
  rows,
}: {
  programName: string;
  month: string;
  total: string;
  donors: number | string;
  rows: Array<Record<string, any>>;
}) => {
  const donorRows =
    rows.length > 0
      ? rows
          .map(
            (row, index) => `
          <tr class="${index % 2 === 0 ? 'odd' : 'even'}">
            <td>${escapeHtml(row.firstName || '—')}</td>
            <td>${escapeHtml(row.lastName || '—')}</td>
            <td class="mono">${escapeHtml(row.phone || '—')}</td>
            <td class="amount">${escapeHtml(formatDonorAmount(row.amount))}</td>
          </tr>`
          )
          .join('')
      : `
          <tr>
            <td colspan="4" class="empty">No donating users for this period</td>
          </tr>`;

  const reportMarkup = `
<style>
  * { box-sizing: border-box; }
  .donation-report-root {
    width: 700px;
    margin: 0;
    padding: 16px;
    background: #eef2ff;
    color: #111827;
    font-family: Arial, Helvetica, sans-serif;
    line-height: 1.45;
  }
  .donation-report-root .page {
    width: 100%;
    background: #ffffff;
    border-radius: 14px;
    overflow: visible;
    border: 1px solid #dbe3ff;
  }
  .donation-report-root .hero {
    background: #1133BB;
    color: #fff;
    padding: 24px 24px 22px;
    border-radius: 14px 14px 0 0;
  }
  .donation-report-root .hero-label {
    display: block;
    margin: 0 0 10px 0;
    padding: 0;
    border: 0;
    background: none;
    font-size: 12px;
    font-weight: 600;
    color: rgba(255,255,255,0.9);
    line-height: 1.3;
  }
  .donation-report-root .hero h1 {
    margin: 0 0 8px;
    font-size: 26px;
    font-weight: 700;
    line-height: 1.2;
  }
  .donation-report-root .hero p {
    margin: 0;
    opacity: 0.95;
    font-size: 13px;
  }
  .donation-report-root .content { padding: 22px 24px 24px; }
  .donation-report-root .cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 22px;
  }
  .donation-report-root .card {
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px 14px;
  }
  .donation-report-root .card .label {
    display: block;
    font-size: 11px;
    color: #6b7280;
    margin-bottom: 4px;
    font-weight: 700;
  }
  .donation-report-root .card .value {
    display: block;
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    word-break: break-word;
  }
  .donation-report-root .card.highlight {
    background: #ecfdf5;
    border-color: #a7f3d0;
  }
  .donation-report-root .card.highlight .value { color: #047857; }
  .donation-report-root .section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
  }
  .donation-report-root .section-head h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
  }
  .donation-report-root .section-head span {
    font-size: 11px;
    color: #6b7280;
    background: #f3f4f6;
    border-radius: 999px;
    padding: 4px 10px;
    font-weight: 700;
    white-space: nowrap;
  }
  .donation-report-root table.donors {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
  }
  .donation-report-root table.donors thead th {
    background: #1133BB;
    color: #fff;
    text-align: left;
    padding: 10px 12px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: normal;
    text-transform: none;
  }
  .donation-report-root table.donors thead th.amount-head {
    text-align: right;
  }
  .donation-report-root table.donors tbody td {
    padding: 10px 12px;
    font-size: 13px;
    border-top: 1px solid #eef2f7;
    vertical-align: middle;
    word-break: break-word;
  }
  .donation-report-root table.donors tbody tr.even td { background: #f8fafc; }
  .donation-report-root td.amount {
    text-align: right;
    font-weight: 700;
    color: #1133BB;
  }
  .donation-report-root td.mono {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 12px;
    color: #374151;
  }
  .donation-report-root td.empty {
    text-align: center;
    color: #6b7280;
    padding: 24px 12px !important;
    background: #f9fafb;
  }
  .donation-report-root .footer {
    margin-top: 18px;
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    font-size: 11px;
    color: #9ca3af;
  }
</style>
<div class="donation-report-root">
  <div class="page">
    <div class="hero">
      <div class="hero-label">SaversApp Partner Portal</div>
      <h1>Donation Report</h1>
      <p>Monthly donation summary and donor details for ${escapeHtml(month)}</p>
    </div>

    <div class="content">
      <div class="cards">
        <div class="card">
          <span class="label">Program Name</span>
          <span class="value">${escapeHtml(programName)}</span>
        </div>
        <div class="card">
          <span class="label">Period</span>
          <span class="value">${escapeHtml(month)}</span>
        </div>
        <div class="card highlight">
          <span class="label">Total Value</span>
          <span class="value">${escapeHtml(total)}</span>
        </div>
        <div class="card">
          <span class="label">Donors</span>
          <span class="value">${escapeHtml(donors)}</span>
        </div>
      </div>

      <div class="section-head">
        <h2>Donating Users</h2>
        <span>${escapeHtml(rows.length)} records</span>
      </div>

      <table class="donors">
        <thead>
          <tr>
            <th style="width:24%;">First Name</th>
            <th style="width:24%;">Last Name</th>
            <th style="width:32%;">Phone</th>
            <th class="amount-head" style="width:20%;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${donorRows}
        </tbody>
      </table>

      <div class="footer">
        <span>Generated from SaversApp Partner Portal</span>
        <span>${escapeHtml(new Date().toLocaleString())}</span>
      </div>
    </div>
  </div>
</div>`;

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '0';
  container.style.width = '700px';
  container.innerHTML = reportMarkup;
  document.body.appendChild(container);

  const reportElement = container.querySelector(
    '.donation-report-root'
  ) as HTMLElement;

  try {
    await html2pdf()
      .set({
        margin: [10, 10, 10, 10],
        filename: `Donation-Report-${month}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: '#eef2ff',
          scrollX: 0,
          scrollY: 0,
          windowWidth: 700,
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(reportElement)
      .save();
  } finally {
    document.body.removeChild(container);
  }
};

const PartnerErnings = () => {
  const location = useLocation();
  const incomingTab = location.state?.activeTab || '1';
  const [activeTab, setActiveTab] = useState<'1' | '2' | '3' | '4'>(
    incomingTab
  );
  const isEarningsLoading = useEndpointLoading('/usr/p/c/d', 'POST');
  const isDonationSummaryLoading = useEndpointLoading('/usr/p/d/d', 'POST');
  const [customDate, setCustomDate] = useState<FilterByDateWithFlag>({
    startDate: '',
    endDate: '',
    typeName: '',
  });
  const {
    dashboardInfo,
    GraphPartnerPayouts,
    dashboardEarningInfo,
    loadingStates: PartnerLoadingState,
  } = useSelector((state: RootState) => state.partner);
  const {
    graphPartnerEarnings,
    graphMemberEarnings,
    campaignData,
    graphDonationTotals,
    donationReports,
    loadingStates,
  } = useSelector((state: RootState) => state.reporting);
  const userDetails = useSelector((state: RootState) => state.auth.userDetails);
  const dispatch = useAppDispatch();
  const [downloadingPayoutId, setDownloadingPayoutId] = useState<number | null>(
    null
  );
  const [partnerData, setPartnerData] = useState<any>(null);
  const [donationData, setDonationData] = useState<any>(null);
  const programName =
    userDetails?.partner?.name || userDetails?.companyName || 'Program';

  const handleDownloadMonthReport = async (row: {
    payoutId?: number | null;
    month?: string;
    total?: string;
    donors?: number | string;
  }) => {
    if (!row.payoutId) return;
    setDownloadingPayoutId(row.payoutId);
    try {
      const rows = await dispatch(ExportDonationReport(row.payoutId));
      await downloadDonationMonthReport({
        programName,
        month: row.month || String(row.payoutId),
        total: row.total || '$0.00',
        donors: row.donors ?? 0,
        rows: Array.isArray(rows) ? rows : [],
      });
    } finally {
      setDownloadingPayoutId(null);
    }
  };

  useEffect(() => {
    if (!dashboardInfo) {
      dispatch(GetDashboardInfo());
    }
  }, [dashboardInfo, dispatch]);
  useEffect(() => {
    if (!dashboardEarningInfo) {
      dispatch(GetDashboardEarningInfo());
    }
  }, [dashboardEarningInfo, dispatch]);

  useEffect(() => {
    const { typeName, ...date } = customDate;
    if (activeTab !== '3') {
      if (typeName === 'PartnerEarnings') {
        dispatch(GetGraphPartnerEarnings(date));
      } else if (!graphPartnerEarnings && activeTab === '1') {
        dispatch(GetGraphPartnerEarnings());
      }

      if (typeName === 'PartnerPayouts') {
        dispatch(GetGraphPartnerPayouts(date));
      } else if (!GraphPartnerPayouts && activeTab === '1') {
        dispatch(GetGraphPartnerPayouts());
      }
      if (typeName === 'MemberEarnings') {
        dispatch(GetGraphMemberEarnings(date));
      } else if (!graphMemberEarnings && activeTab === '2') {
        dispatch(GetGraphMemberEarnings());
      }
      if (typeName === 'DonationTotals') {
        dispatch(GetGraphDonationTotals(date));
        dispatch(GetDonationReports(date));
      } else if (activeTab === '4') {
        if (!graphDonationTotals) {
          dispatch(GetGraphDonationTotals());
        }
        if (!donationReports) {
          dispatch(GetDonationReports());
        }
      }
    }
    if (activeTab === '3' && !campaignData) {
      dispatch(GetGraphCampaignData());
    }

    if (typeName) {
      setCustomDate(prev => ({ ...prev, typeName: '' }));
    }
  }, [
    activeTab,
    customDate,
    dispatch,
    graphPartnerEarnings,
    graphMemberEarnings,
    GraphPartnerPayouts,
    campaignData,
    graphDonationTotals,
    donationReports,
  ]);

  useEffect(() => {
    return () => {
      dispatch(setGraphMemberEarnings(null));
      dispatch(setGraphPartnerEarnings(null));
      dispatch(setGraphCampaignData(null));
      dispatch(setGraphDonationTotals(null));
      dispatch(setDonationReports(null));
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.post('/usr/p/c/d');
        setPartnerData(response);
      } catch (error) {
        console.error('API Error:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab !== '4') return;

    const fetchDonationSummary = async () => {
      try {
        const response = await api.post('/usr/p/d/d');
        setDonationData(response);
      } catch (error) {
        console.error('Donation summary API Error:', error);
      }
    };

    fetchDonationSummary();
  }, [activeTab]);

  // Determine which boxes to show based on the conditions
  const shouldShowBoxB = partnerData?.receivePayout === '';
  const shouldShowBoxC = !shouldShowBoxB;
  const donationReportRows: DonationReportRow[] = Array.isArray(donationReports)
    ? donationReports
    : [];
  const donationTableData = donationReportRows.map(row => ({
    month: row.name,
    total: formatDonationAmount(row.total, row.currency),
    donors: Number(row.donors) || 0,
    payoutId: row.payoutId ?? null,
  }));
  const isSummaryLoading =
    activeTab === '1'
      ? isEarningsLoading
      : activeTab === '4'
        ? isDonationSummaryLoading
        : false;

  return (
    <div className='mb-8'>
      {/* Toggle Button */}
      <HistoryRouting history='Reporting/Earnings Data' />
      <div className='space-y-10'>
        {/* A - B - C - D Boxes */}
        {isSummaryLoading ? (
          <div className='ml-12 my-4'>
            <Spinner />
          </div>
        ) : activeTab === '1' ? (
          <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3'>
            {/* A - Life time program payouts (always shown) */}
            <EarningInfoBox
              title='Lifetime Partner Payouts'
              value={dashboardEarningInfo?.partnerPayouts?.value || '$0.00'}
            />

            {shouldShowBoxB && (
              <EarningInfoBox
                title='Next Payout'
                value={partnerData?.available || '$0.00'}
                // badge='NEW'
                extraContent={
                  <p className='text-sm text-gray-600'>
                    Date: {formatDateString(partnerData?.payoutDate) ?? 'N/A'}
                  </p>
                }
              />
            )}

            {shouldShowBoxC && (
              <EarningInfoBox
                title='Confirmed Partner Earnings'
                value={
                  dashboardEarningInfo?.partnerConfirmedEarnings?.value ||
                  '$0.00'
                }
                tooltip='You need to reach $25 to qualify for payouts'
              />
            )}

            <EarningInfoBox
              title='Pending Partner Earnings'
              value={
                dashboardEarningInfo?.partnerPendingEarnings?.value || '$0.00'
              }
              tooltip='Earnings that are still being processed'
            />
          </div>
        ) : activeTab === '4' ? (
          <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3'>
            <EarningInfoBox
              title='Lifetime Donations'
              value={formatDonationAmount(donationData?.totalEarned)}
            />
            <EarningInfoBox
              title='Next Donation'
              value={formatDonationAmount(donationData?.available)}
              extraContent={
                <p className='text-sm text-gray-600'>
                  Date: {formatDateString(donationData?.payoutDate) ?? 'N/A'}
                </p>
              }
            />
            <EarningInfoBox
              title='Pending Donations'
              value={formatDonationAmount(donationData?.pending)}
            />
          </div>
        ) : (
          <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3'>
            <EarningInfoBox
              title='Lifetime Member Payouts'
              value={dashboardEarningInfo?.userPayouts?.value || '$0.00'}
            />
            <EarningInfoBox
              title='Confirmed Member Cashback'
              value={
                dashboardEarningInfo?.usersConfirmedEarnings?.value || '$0.00'
              }
            />
            <EarningInfoBox
              title='Pending Member Cashback'
              value={
                dashboardEarningInfo?.usersPendingEarnings?.value || '$0.00'
              }
            />
          </div>
        )}
        <div className='border border-gray-200 primary-shadow px-7 pt-7 pb-5'>
          <div className='flex gap-10'>
            <Button
              className={` ${
                activeTab === '1'
                  ? 'bg-[#00C389] text-white'
                  : 'text-black bg-white'
              }    w-full`}
              onClick={() => setActiveTab('1')}
            >
              Partner Cashback
            </Button>
            <Button
              className={` ${
                activeTab === '2'
                  ? 'bg-[#00C389] text-white'
                  : 'text-black bg-white'
              }    w-full`}
              onClick={() => setActiveTab('2')}
            >
              Member Cashback
            </Button>
            <Button
              className={` ${
                activeTab === '4'
                  ? 'bg-[#00C389] text-white'
                  : 'text-black bg-white'
              }    w-full`}
              onClick={() => setActiveTab('4')}
            >
              Donations
            </Button>
           {/* <Button
              className={` ${
                activeTab === '3'
                  ? 'bg-[#00C389] text-white'
                  : 'text-black bg-white'
              }    w-full`}
              onClick={() => setActiveTab('3')}
            >
              Program Booster Earnings
            </Button>*/}
          </div>
          {/* Display Charts or List View */}
          <div className='grid grid-cols-1 '>
            {activeTab === '1' && (
              <div className='space-y-6 my-6'>
                <BarchartComp
                  data={graphPartnerEarnings}
                  title='Partner Earnings'
                  dataKey1='pending'
                  name1='pending'
                  dataKey2='confirmed'
                  name2='confirmed'
                  dataKey3='rejected'
                  name3='rejected/cancelled/refunded'
                  chartName='PartnerEarnings'
                  setCustomDate={setCustomDate}
                  dollar={true}
                  isFiltering={true}
                  description='Monthly Partner Earnings'
                  isLoading={loadingStates['GetGraphPartnerEarnings']}
                  showExport
                  isShadowBox
                  isStack
                />
                <BarchartComp
                  title='Partner Payouts'
                  data={GraphPartnerPayouts}
                  dataKey1='amount'
                  name1='Partner Payouts'
                  chartName='PartnerPayouts'
                  dollar={true}
                  isFiltering={true}
                  isOneColumn={true}
                  description='Monthly Payouts'
                  setCustomDate={setCustomDate}
                  isLoading={PartnerLoadingState['GetGraphPartnerPayouts']}
                  showExport
                  isShadowBox
                />
              </div>
            )}
            {activeTab === '2' && (
              <BarchartComp
                data={graphMemberEarnings}
                dataKey1='pending'
                name1='pending'
                dataKey2='confirmed'
                name2='confirmed'
                dataKey3='rejected'
                name3='rejected'
                chartName='MemberEarnings'
                setCustomDate={setCustomDate}
                dollar={true}
                isFiltering={true}
                description='Monthly Active Members Earnings'
                isLoading={loadingStates['GetGraphMemberEarnings']}
                showExport
                isStack
              />
            )}
            {activeTab === '3' && (
              <BarchartComp
                data={campaignData}
                dataKey1='qualifiedUsers'
                name1='Qualified Sign-Ups'
                dataKey2='notQualifiedUsers'
                name2='Unqualified Sign-ups'
                chartName='MemberEarnings'
                setCustomDate={setCustomDate}
                description='Campaign Rewards'
                isLoading={loadingStates['GetGraphCampaignData']}
                showExport
                isStack
              />
            )}
            {activeTab === '4' && (
              <div className='space-y-6 my-6'>
                <BarchartComp
                  title='Donation Totals by Month'
                  data={graphDonationTotals}
                  dataKey1='amount'
                  name1='Donations'
                  chartName='DonationTotals'
                  dollar={true}
                  isFiltering={true}
                  isOneColumn={true}
                  barColor1='#00C389'
                  description='Donation Totals by Month'
                  setCustomDate={setCustomDate}
                  isLoading={loadingStates['GetGraphDonationTotals']}
                  showExport
                  isShadowBox
                />

                {loadingStates['GetDonationReports'] ? (
                  <div className='py-6'>
                    <Spinner />
                  </div>
                ) : donationTableData.length > 0 ? (
                  <Table
                    data={donationTableData}
                    config={donationReportsConfig}
                    showExportButton={true}
                    width='w-full'
                    enableSorting={false}
                    showSearch={false}
                    title='Monthly Donation Reports'
                    actionsLabel='Report'
                    rowActions={row => {
                      const isDownloading =
                        downloadingPayoutId != null &&
                        downloadingPayoutId === row.payoutId;
                      return (
                        <button
                          type='button'
                          title={
                            !row.payoutId
                              ? 'No report available'
                              : isDownloading
                                ? 'Downloading report'
                                : 'Download report'
                          }
                          aria-label={
                            isDownloading
                              ? 'Downloading report'
                              : 'Download report'
                          }
                          className='rounded-lg border border-gray-200 h-9 w-9 flex items-center justify-center bg-white text-[#1133BB] hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed'
                          disabled={!row.payoutId || isDownloading}
                          onClick={() => handleDownloadMonthReport(row)}
                        >
                          {isDownloading ? (
                            <MdOutlineHourglassTop
                              size={18}
                              className='animate-pulse'
                            />
                          ) : (
                            <MdDownload size={18} />
                          )}
                        </button>
                      );
                    }}
                  />
                ) : (
                  <p className='italic text-gray-600 text-center py-6'>
                    No data available
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerErnings;
